import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Filter, ShoppingBag, Plus, Star, Eye, Heart } from 'lucide-react';
import { productsApi, productReviewsApi } from '../lib/api';
import { useCart } from '../lib/cartContext';
import { useSaved } from '../lib/SavedContext';
import { useAuth } from '../lib/AuthContext';
import RecentlyViewedProducts from '../components/shop/RecentlyViewedProducts';
import QuickViewModal from '../components/shop/QuickViewModal';
import { ProductCatalogSchema } from '../components/seo/SchemaMarkup';
import { NewArrivalsStrip } from '../components/shop/NewArrivalsStrip';
import { NextDropBannerLine, WomensDropBannerLine } from '../components/shop/DropCountdown';

const categories = ['All', 'T-Shirts', 'Hoodies', 'Hats', 'HYROX Apparel'];

const ProductCard = ({ product, reviewStats, onQuickView }) => {
  const { addItem, loading } = useCart();
  const { isSaved, toggleSaved } = useSaved();
  const { user } = useAuth();
  const [adding, setAdding] = useState(false);

  const hasDiscount = Boolean(product.compare_at_price && product.compare_at_price > product.price);
  const discountPercent = hasDiscount
    ? Math.round((1 - product.price / product.compare_at_price) * 100)
    : 0;

  const sizedVariants = (product.variants || []).filter((v) => v.title && v.title !== 'Default Title');
  const soldOut = sizedVariants.length > 0
    ? sizedVariants.every((v) => v.available === false || (typeof v.inventory_quantity === 'number' && v.inventory_quantity <= 0))
    : (typeof product.inventory_count === 'number' && product.inventory_count <= 0);

  const stats = reviewStats?.[product.id];
  const saved = isSaved(product.id, 'product');

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAdding(true);
    await addItem(product.id);
    setAdding(false);
  };

  const handleToggleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { window.location.href = '/auth'; return; }
    toggleSaved({
      type: 'product',
      item_id: product.id,
      title: product.name,
      image: product.images?.[0] || '',
      category: product.category || '',
      price: product.price,
      slug: product.slug,
    });
  };

  return (
    <div className="group card-product" data-testid={`product-card-${product.slug}`}>
      <Link to={`/shop/${product.slug}`} className="block no-underline">
        <div className="relative aspect-square overflow-hidden bg-zinc-900">
          <img
            src={product.images?.[0] || 'https://placehold.co/600x600/0a0a0a/ffffff?text=Product'}
            alt={product.name}
            className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
          {soldOut && (
            <span className="absolute top-3 left-3 px-2 py-1 text-xs uppercase tracking-wider bg-zinc-800 text-white font-bold border border-zinc-600 z-[1]" data-testid={`sold-out-badge-${product.slug}`}>
              Sold Out — Notify Me
            </span>
          )}
          {hasDiscount && !soldOut && (
            <span className="absolute top-3 left-3 px-2 py-1 text-xs uppercase tracking-wider bg-[#E63946] text-white">
              -{discountPercent}%
            </span>
          )}
          {product.coming_soon && (
            <span className="absolute top-3 left-3 px-2 py-1 text-xs uppercase tracking-wider bg-[#C7FA3E] text-black font-bold" data-testid={`coming-soon-badge-${product.slug}`}>
              Coming Soon
            </span>
          )}
          {/* Wishlist Heart */}
          <button
            onClick={handleToggleWishlist}
            className={`absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full transition-all duration-300 z-10 ${
              saved ? 'bg-[#E63946] text-white' : 'bg-black/40 backdrop-blur-sm text-zinc-300 opacity-0 group-hover:opacity-100 hover:bg-[#E63946]/80 hover:text-white'
            }`}
            data-testid={`wishlist-btn-${product.slug}`}
            aria-label={saved ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <Heart className={`w-4 h-4 ${saved ? 'fill-white' : ''}`} />
          </button>
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="flex gap-2">
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onQuickView(product); }}
                className="flex-shrink-0 w-11 h-11 flex items-center justify-center bg-zinc-800 text-white hover:bg-zinc-700 transition-colors"
                data-testid={`quick-view-${product.slug}`}
                aria-label="Quick view"
              >
                <Eye className="w-4 h-4" />
              </button>
              <button
                onClick={handleAddToCart}
                disabled={loading || adding}
                className="flex-1 btn-ninja py-3 text-sm flex items-center justify-center gap-2"
                data-testid={`add-to-cart-${product.slug}`}
              >
                {adding ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Add to Cart
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="p-4">
          <span className="text-xs text-zinc-500 uppercase tracking-wider">
            {product.category || product.product_type}
          </span>
          <h3 className="text-white text-lg mt-1 font-heading group-hover:text-[#E63946] transition-colors duration-300">
            {product.name}
          </h3>
          {stats && stats.total > 0 && (
            <div className="flex items-center gap-1.5 mt-1" data-testid={`product-rating-${product.slug}`}>
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map(i => (
                  <Star key={i} className={`w-3 h-3 ${i <= Math.round(stats.average_rating) ? 'text-[#E63946] fill-[#E63946]' : 'text-zinc-700'}`} />
                ))}
              </div>
              <span className="text-zinc-500 text-xs">({stats.total})</span>
            </div>
          )}
          <div className="flex items-center gap-3 mt-3">
            <span className="text-[#E63946] text-xl font-semibold">
              ${product.price?.toFixed(2)}
            </span>
            {hasDiscount && (
              <span className="text-zinc-600 line-through text-sm">
                ${product.compare_at_price.toFixed(2)}
              </span>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
};

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('featured');
  const [loading, setLoading] = useState(true);
  const [reviewStats, setReviewStats] = useState({});
  const [quickViewProduct, setQuickViewProduct] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await productsApi.getAll({ active: true });
        if (response.data && response.data.length > 0) {
          setProducts(response.data);
          // Fetch review stats for all products in parallel
          const statsMap = {};
          await Promise.all(response.data.map(async (p) => {
            try {
              const res = await productReviewsApi.getReviews(p.id);
              if (res.data.stats && res.data.stats.total > 0) {
                statsMap[p.id] = res.data.stats;
              }
            } catch (e) { console.error('Failed to fetch reviews for product', p.id, e); }
          }));
          setReviewStats(statsMap);
        }
      } catch (error) {
        console.error('Failed to fetch products', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const HYROX_CATEGORIES = ['Tank Tops', 'Shorts', 'Sports Bras'];
  const filteredProducts = selectedCategory === 'All'
    ? products
    : selectedCategory === 'HYROX Apparel'
      ? products.filter(p => HYROX_CATEGORIES.includes(p.category))
      : products.filter(p => p.category === selectedCategory);

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-low': return a.price - b.price;
      case 'price-high': return b.price - a.price;
      case 'name': return a.name.localeCompare(b.name);
      default: return 0;
    }
  });

  return (
    <main className="pt-20 min-h-screen" data-testid="shop-page">
      <ProductCatalogSchema products={products} />
      <section className="container-ninja section-spacing">
        <div className="max-w-3xl">
          <p className="text-ninja-label text-[#E63946] mb-4">Official Merch</p>
          <h1 className="text-white mb-6">
            NinjAthlete <span className="text-gradient-ninja">Merch</span>
          </h1>
          <p className="text-zinc-400 text-lg leading-relaxed">
            Premium apparel and accessories. Represent the grind with NinjAthlete gear — tees, hoodies, and snapbacks built for athletes.
          </p>
        </div>
      </section>

      {/* Gear Box subscription banner */}
      <section className="container-ninja pb-12">
        <Link
          to="/gear-box"
          className="group relative flex flex-col md:flex-row md:items-center justify-between gap-5 border border-[#C7FA3E]/30 bg-zinc-900/60 hover:border-[#C7FA3E]/60 transition-colors p-6 md:p-8"
          data-testid="gearbox-banner"
        >
          <div>
            <p className="text-[#C7FA3E] text-xs uppercase tracking-[0.25em] mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
              New · Monthly subscription
            </p>
            <h2 className="text-white text-2xl md:text-3xl mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>
              The NinjAthlete Gear Box
            </h2>
            <p className="text-zinc-400 text-sm">Fresh apparel, training accessories and recovery gear delivered monthly. Standard $39/mo · Elite $79/mo · cancel anytime.</p>
          </div>
          <span
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#C7FA3E] text-black text-xs font-bold uppercase tracking-widest group-hover:gap-3.5 transition-all whitespace-nowrap self-start md:self-center"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            Get the box →
          </span>
        </Link>
      </section>

      {/* City Series drop banner */}
      <section className="container-ninja pb-12">
        <Link
          to="/city-series"
          className="group relative flex flex-col md:flex-row md:items-center justify-between gap-5 border border-zinc-800 bg-zinc-900/60 hover:border-[#C7FA3E]/50 transition-colors p-6 md:p-8"
          data-testid="city-series-banner"
        >
          <div>
            <p className="text-[#E63946] text-xs uppercase tracking-[0.25em] mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
              Drop 01 · Live now
            </p>
            <h2 className="text-white text-2xl md:text-3xl mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>
              City Series — Run Club Tees
            </h2>
            <p className="text-zinc-400 text-sm">14 cities. 14 unique limited-run designs — each city gets its own Ninja Run Club shirt. Oversized heavyweight tees. $42 · S–2XL.</p>
            <NextDropBannerLine />
          </div>
          <span
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#C7FA3E] text-black text-xs font-bold uppercase tracking-widest group-hover:gap-3.5 transition-all whitespace-nowrap self-start md:self-center"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            Shop the drop →
          </span>
        </Link>
      </section>

      {/* Women's Collection drop banner */}
      <section className="container-ninja pb-12">
        <Link
          to="/womens-collection"
          className="group relative flex flex-col md:flex-row md:items-center justify-between gap-5 border border-zinc-800 bg-zinc-900/60 hover:border-[#C7FA3E]/50 transition-colors p-6 md:p-8"
          data-testid="womens-collection-banner"
        >
          <div>
            <p className="text-[#E63946] text-xs uppercase tracking-[0.25em] mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
              Drop 02 · Waitlist open
            </p>
            <h2 className="text-white text-2xl md:text-3xl mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>
              Women&apos;s Collection — Muscle Mommy &amp; More
            </h2>
            <p className="text-zinc-400 text-sm">The first NinjAthlete women&apos;s line — Muscle Mommy oversized tee, strappy sports bra and high-waist yoga shorts. $38–$44.</p>
            <WomensDropBannerLine />
          </div>
          <span
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#C7FA3E] text-black text-xs font-bold uppercase tracking-widest group-hover:gap-3.5 transition-all whitespace-nowrap self-start md:self-center"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            Join the waitlist →
          </span>
        </Link>
      </section>

      <section className="container-ninja pb-2">
        <NewArrivalsStrip />
      </section>

      <section className="container-ninja pb-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 text-sm uppercase tracking-wider font-heading transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-[#E63946] text-white'
                    : 'bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                data-testid={`shop-filter-${category.toLowerCase().replace(/\s/g, '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Filter className="w-4 h-4 text-zinc-500" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-zinc-900 text-zinc-400 px-4 py-2 text-sm uppercase tracking-wider font-heading border-none focus:ring-1 focus:ring-[#E63946]"
              data-testid="shop-sort"
            >
              <option value="featured">Featured</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="name">Name</option>
            </select>
          </div>
        </div>
        <p className="text-zinc-600 text-sm mt-4">{sortedProducts.length} products</p>
      </section>

      <section className="container-ninja pb-24">
        {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
        {loading ? (
          <div className="text-center py-16">
            <div className="w-8 h-8 border-2 border-[#E63946] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-zinc-500">Loading products...</p>
          </div>
        ) : sortedProducts.length > 0 ? (
          <div className="product-grid">
            {sortedProducts.map((product) => (
              <ProductCard key={product.id || product.slug} product={product} reviewStats={reviewStats} onQuickView={setQuickViewProduct} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <ShoppingBag className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">No products found in this category.</p>
          </div>
        )}
      </section>

      {/* Recently Viewed Products */}
      <RecentlyViewedProducts />

      <section className="bg-zinc-900/50 py-16">
        <div className="container-ninja">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { label: 'Free Shipping', sub: 'Orders over $99' },
              { label: '30-Day Returns', sub: 'No questions asked' },
              { label: 'Secure Checkout', sub: 'SSL encrypted' },
              { label: 'Quality Guaranteed', sub: 'Or your money back' }
            ].map((badge) => (
              <div key={badge.label}>
                <h4 className="text-white text-lg mb-1 font-heading">{badge.label}</h4>
                <p className="text-zinc-500 text-sm">{badge.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Quick View Modal */}
      {quickViewProduct && (
        <QuickViewModal product={quickViewProduct} onClose={() => setQuickViewProduct(null)} />
      )}
    </main>
  );
}
